import React, { FormEvent, useEffect, useRef, useState } from "react";
import { twMerge } from 'tailwind-merge';
import { getBusinessAccount } from "../apis/bussiness-accounts/get-bussiness-accounts";
import { getAllDrivers } from "../apis/drivers/get-all-drivers";
import { getAllUsers } from "../apis/users/get-all-users";
import { UserType } from "../pages/DashboardUsers";
import { IBussinessAccountDocs, IDocsDriver } from "../utils/types";
import { formatDate2 } from "../utils/helpers";
import DataTable, { TBody, TH, THead, TR } from "./DataTable";
import Modal from './Modal';
import Pagination from "./Pagination";
import Spinner from "./Spinner";
import Button from './Button';
import Input from './Input';

type PropTypes = {
  isOpen: boolean;
  closeModal: () => void;
  userType: string;
  addToTarget: (id: string, name: string) => void;
  removeFromTarget: (id: string) => void;
  targets: string[];
};

type ChildTableType = Pick<PropTypes, "addToTarget"> &
  Pick<PropTypes, "removeFromTarget"> &
  Pick<PropTypes, "targets">;

const SpecificTable = ({
  isOpen,
  closeModal,
  userType,
  addToTarget,
  removeFromTarget,
  targets,
}: PropTypes) => {
  return (
    <Modal
      heading={`Select ${userType === "user"
        ? "Users"
        : userType === "driver"
          ? "Drivers"
          : "Business"
        }`
      }
      isVisible={isOpen}
      onHide={closeModal}
    >
      {userType === "user" ? (
        <UsersTable
          targets={targets}
          removeFromTarget={removeFromTarget}
          addToTarget={addToTarget}
        />
      ) : userType === "driver" ? (
        <DriversTable
          targets={targets}
          removeFromTarget={removeFromTarget}
          addToTarget={addToTarget}
        />
      ) : (
        <BusinessAccountsTable
          targets={targets}
          removeFromTarget={removeFromTarget}
          addToTarget={addToTarget}
        />
      )}
      <div className="flex justify-end mt-3 mr-3">
        <Button
          onClick={closeModal}
          className="w-32 min-h-0 h-10 md:h-10 "
        >
          Save
        </Button>
      </div>
    </Modal>
  );
};

export default SpecificTable;

const UsersTable = ({
  addToTarget,
  removeFromTarget,
  targets,
}: ChildTableType) => {
  const [users, setUsers] = useState<UserType[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const searchRef = useRef("");

  const getUsers = async () => {
    setLoading(true);
    const res = await getAllUsers(
      new URLSearchParams({
        limit: "10",
        page: currentPage.toString(),
        search: search || "",
      })
    );
    if (res.success) {
      setUsers(res?.data?.docs);
      setTotalPages(res?.data?.totalPages);
    }
    setLoading(false);
  };

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    setSearch(searchRef.current);
  };

  useEffect(() => {
    getUsers();
  }, [currentPage, search]);

  return (
    <div>
      <form onSubmit={handleSearch} className="mb-4 px-8">
        <div className="flex items-center gap-2 justify-center">
          <Input
            onChange={(e) => (searchRef.current = e.target.value)}
            containerClassName='w-2/3 h-full md:w-auto'
            outerClassName='h-full'
            className="w-full md:w-52 outline-none input-primary h-8 md:h-10 rounded-lg"
            placeholder="search"
          />
          <Button
            type="submit"
            className="w-1/3 md:w-32 min-h-0 h-8 md:h-10"
          >
            Search
          </Button>
        </div>
      </form>
      {loading ? (
        <Spinner />
      ) : !loading && users.length === 0 ? (
        <p className='text-center'>No users found </p>
      ) : (

        <div className="px-5">
          < div className="block md:hidden">
            {users?.map((user) => (
              <div
                key={user?._id}
                onClick={() => {
                  if (targets.includes(user?._id)) {
                    removeFromTarget(user?._id);
                  } else {
                    addToTarget(user?._id, user.fullName);
                  }
                }}
                className={twMerge(
                  "cursor-pointer p-4 mb-2 rounded-lg shadow-sm border border-gray-100 bg-white flex flex-col gap-1",
                  targets.includes(user?._id) && "bg-primary border-primary"
                )}
              >
                <p className={twMerge(
                  "font-semibold text-sm truncate",
                  targets.includes(user?._id) ? "text-white" : "text-black"
                )}>
                  {user.fullName}
                </p>
                <p className={twMerge(
                  "text-sm truncate",
                  targets.includes(user?._id) ? "text-white" : "text-gray-500"
                )}>
                  {user.email}
                </p>
                <p className={twMerge(
                  "text-sm",
                  targets.includes(user?._id) ? "text-white" : "text-gray-500"
                )}>
                  {user.phone}
                </p>
                <p className={twMerge(
                  "text-sm",
                  targets.includes(user?._id) ? "text-white" : "text-gray-500"
                )}>
                  {formatDate2(user?.createdAt || "")}
                </p>
              </div>
            ))}
          </div>

          <div className="hidden md:block">
            <DataTable>
              <THead>
                <TR>
                  <TH>Username</TH>
                  <TH>Email</TH>
                  <TH>Phone</TH>
                  <TH>Created At</TH>
                </TR>
              </THead>
              <TBody>
                {users?.map((user) => (
                  <TR
                    key={user?._id}
                    className={twMerge(
                      "cursor-pointer",
                      targets.includes(user?._id) && "bg-primary text-white"
                    )}
                    onClick={() => {
                      if (targets.includes(user?._id)) {
                        removeFromTarget(user?._id);
                      } else {
                        addToTarget(user?._id, user.fullName);
                      }
                    }}
                  >
                    <TH className="align-middle">
                      <p className={twMerge(
                        "truncate whitespace-nowrap max-w-[180px] text-md font-normal",
                        targets.includes(user?._id) ? "text-white" : "text-black"
                      )}>
                        {user.fullName}
                      </p>
                    </TH>
                    <TH className="align-middle">
                      <p className={twMerge(
                        "text-md max-w-[240px] whitespace-nowrap truncate font-normal",
                        targets.includes(user?._id) ? "text-white" : "text-black"
                      )}>
                        {user.email}
                      </p>
                    </TH>
                    <TH className={twMerge(
                      "align-middle font-normal whitespace-nowrap",
                      targets.includes(user?._id) ? "text-white" : "text-black"
                    )}>
                      {user.phone}
                    </TH>
                    <TH className={twMerge(
                      "align-middle font-normal whitespace-nowrap",
                      targets.includes(user?._id) ? "text-white" : "text-black"
                    )}>
                      {formatDate2(user?.createdAt || "")}
                    </TH>
                  </TR>
                ))}
              </TBody>
            </DataTable>
          </div>

        </div>

      )}
      <div className="mt-4">
        <Pagination
          page={totalPages}
          handlePageClick={(e: { selected: number }) =>
            setCurrentPage(e.selected + 1)
          }
        />
      </div>
    </div >
  );
};

const DriversTable = ({
  addToTarget,
  removeFromTarget,
  targets,
}: ChildTableType) => {
  const [drivers, setDrivers] = useState<IDocsDriver[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLaoding] = useState(false);

  const searchRef = useRef("");

  const getDrivers = async () => {
    setLaoding(true);
    const res = await getAllDrivers({
      limit: 10,
      page: currentPage,
      search,
    });
    if (res.success === true) {
      setDrivers(res.data?.docs);
      setTotalPages(res.data?.totalPages);
    }
    setLaoding(false);
  };

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    setSearch(searchRef.current);
  };

  useEffect(() => {
    getDrivers();
  }, [currentPage, search]);

  return (
    <div>
      <form onSubmit={handleSearch} className="mb-4 px-8">
        <div className="flex items-center gap-2 justify-center">
          <Input
            onChange={(e) => (searchRef.current = e.target.value)}
            containerClassName='w-2/3 h-full md:w-auto'
            outerClassName='h-full'
            className="w-full md:w-52 outline-none input-primary h-8 md:h-10 rounded-lg"
            placeholder="search"
          />
          <Button
            type="submit"
            className="w-1/3 md:w-32 min-h-0 h-8 md:h-10"
          >
            Search
          </Button>
        </div>
      </form>
      {loading ? (
        <Spinner />
      ) : !loading && drivers.length === 0 ? (
        <p className='text-center'>No drivers found </p>
      ) : (

        <div className="px-5">

          <div className="block md:hidden">
            {drivers?.map((driver) => (
              <div
                key={driver?._id}
                onClick={() => {
                  if (targets.includes(driver?._id)) {
                    removeFromTarget(driver?._id);
                  } else {
                    addToTarget(driver?._id, driver.fullName);
                  }
                }}
                className={twMerge(
                  "cursor-pointer p-4 mb-2 rounded-lg shadow-sm border border-gray-100 bg-white flex flex-col gap-1",
                  targets.includes(driver?._id) && "bg-primary border-primary"
                )}
              >
                <p className={twMerge(
                  "font-semibold text-sm truncate",
                  targets.includes(driver?._id) ? "text-white" : "text-black"
                )}>
                  {driver.fullName}
                </p>
                <p className={twMerge(
                  "text-sm truncate",
                  targets.includes(driver?._id) ? "text-white" : "text-gray-500"
                )}>
                  {driver.email}
                </p>
                <p className={twMerge(
                  "text-sm",
                  targets.includes(driver?._id) ? "text-white" : "text-gray-500"
                )}>
                  {driver.phone}
                </p>
                <p className={twMerge(
                  "text-sm",
                  targets.includes(driver?._id) ? "text-white" : "text-gray-500"
                )}>
                  {formatDate2(driver?.createdAt || "")}
                </p>
              </div>
            ))}
          </div>


          <div className='hidden md:block'>
            <DataTable>
              <THead>
                <TR>
                  <TH>Username</TH>
                  <TH>Email</TH>
                  <TH>Phone</TH>
                  <TH>Created At</TH>
                </TR>
              </THead>
              <TBody>
                {drivers?.map((driver) => (
                  <TR
                    key={driver?._id}
                    className={twMerge(
                      "cursor-pointer",
                      targets.includes(driver?._id) && "bg-primary text-white"
                    )}
                    onClick={() => {
                      if (targets.includes(driver?._id)) {
                        removeFromTarget(driver?._id);
                      } else {
                        addToTarget(driver?._id, driver.fullName);
                      }
                    }}
                  >
                    <TH className="align-middle">
                      <p className={twMerge(
                        "truncate text-md max-w-[180px] font-normal",
                        targets.includes(driver?._id) ? "text-white" : "text-black"
                      )}>
                        {driver.fullName}
                      </p>
                    </TH>
                    <TH className="align-middle">
                      <p className={twMerge(
                        "text-md max-w-[240px] truncate whitespace-nowrap font-normal",
                        targets.includes(driver?._id) ? "text-white" : "text-black"
                      )}>
                        {driver.email}
                      </p>
                    </TH>
                    <TH className={twMerge(
                      "align-middle font-normal whitespace-nowrap",
                      targets.includes(driver?._id) ? "text-white" : "text-black"
                    )}>
                      {driver.phone}
                    </TH>
                    <TH className={twMerge(
                      "align-middle font-normal whitespace-nowrap",
                      targets.includes(driver?._id) ? "text-white" : "text-black"
                    )}>
                      {formatDate2(driver?.createdAt || "")}
                    </TH>
                  </TR>
                ))}
              </TBody>
            </DataTable>
          </div>
        </div>

      )}
      <div className="mt-4">
        <Pagination
          page={totalPages}
          handlePageClick={(e: { selected: number }) =>
            setCurrentPage(e.selected + 1)
          }
        />
      </div>
    </div>
  );
};

const BusinessAccountsTable = ({
  addToTarget,
  removeFromTarget,
  targets,
}: ChildTableType) => {
  const [businessAccounts, setBusinessAccounts] = useState<
    IBussinessAccountDocs[]
  >([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const searchRef = useRef("");

  const getBusinessAccounts = async () => {
    setLoading(true);
    const res = await getBusinessAccount({
      page: currentPage,
      search: search || "",
    });
    if (res?.success) {
      setBusinessAccounts(res.data?.docs);
      setTotalPages(res.data?.totalPages);
    }
    setLoading(false);
  };

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    setSearch(searchRef.current);
  };

  useEffect(() => {
    getBusinessAccounts();
  }, [currentPage, search]);

  return (
    <div>
      <form onSubmit={handleSearch} className="mb-4 px-8">
        <div className="flex items-center gap-2 justify-center">
          <Input
            onChange={(e) => (searchRef.current = e.target.value)}
            containerClassName='w-2/3 h-full md:w-auto'
            outerClassName='h-full'
            className="w-full md:w-52 outline-none input-primary h-8 md:h-10 rounded-lg"
            placeholder="search"
          />
          <Button
            type="submit"
            className="w-1/3 md:w-32 min-h-0 h-8 md:h-10"
          >
            Search
          </Button>
        </div>
      </form>
      {loading ? (
        <Spinner />
      ) : !loading && businessAccounts.length === 0 ? (
        <p className='text-center' >No business accounts found </p>
      ) : (
        <div className="px-5">

          <div className="block md:hidden">
            {businessAccounts?.map((account) => (
              <div
                key={account?._id}
                onClick={() => {
                  if (targets.includes(account?._id)) {
                    removeFromTarget(account?._id);
                  } else {
                    addToTarget(account?._id, account.fullName);
                  }
                }}
                className={twMerge(
                  "cursor-pointer p-4 mb-2 rounded-lg shadow-sm border border-gray-100 bg-white flex flex-col gap-1",
                  targets.includes(account?._id) && "bg-primary border-primary"
                )}
              >
                <p className={twMerge(
                  "font-semibold text-sm truncate",
                  targets.includes(account?._id) ? "text-white" : "text-black"
                )}>
                  {account.fullName}
                </p>
                <p className={twMerge(
                  "text-sm truncate",
                  targets.includes(account?._id) ? "text-white" : "text-gray-500"
                )}>
                  {account.email}
                </p>
                <p className={twMerge(
                  "text-sm",
                  targets.includes(account?._id) ? "text-white" : "text-gray-500"
                )}>
                  {account.businessName || "N/A"}
                </p>
                <p className={twMerge(
                  "text-sm",
                  targets.includes(account?._id) ? "text-white" : "text-gray-500"
                )}>
                  {account.phone}
                </p>
                <p className={twMerge(
                  "text-sm",
                  targets.includes(account?._id) ? "text-white" : "text-gray-500"
                )}>
                  {account.state || "N/A"}
                </p>
              </div>
            ))}
          </div>

          <div className='hidden md:block'>
            <DataTable>
              <THead>
                <TR>
                  <TH>Username</TH>
                  <TH>Email</TH>
                  <TH>Business name</TH>
                  <TH>Phone</TH>
                  <TH>State</TH>
                </TR>
              </THead>
              <TBody>
                {businessAccounts?.map((account) => (
                  <TR
                    key={account?._id}
                    className={twMerge(
                      "cursor-pointer",
                      targets.includes(account?._id) && "bg-primary text-white"
                    )}
                    onClick={() => {
                      if (targets.includes(account?._id)) {
                        removeFromTarget(account?._id);
                      } else {
                        addToTarget(account?._id, account.fullName);
                      }
                    }}
                  >
                    <TH className="align-middle">
                      <div className={twMerge(
                        "max-w-[180px] truncate text-md font-normal",
                        targets.includes(account?._id) ? "text-white" : "text-black"
                      )}>
                        {account.fullName}
                      </div>
                    </TH>
                    <TH className="align-middle">
                      <div className={twMerge(
                        "text-md max-w-[240px] text-ellipsis truncate whitespace-nowrap font-normal",
                        targets.includes(account?._id) ? "text-white" : "text-black"
                      )}>
                        {account.email}
                      </div>
                    </TH>
                    <TH className={twMerge(
                      "font-normal",
                      targets.includes(account?._id) ? "text-white" : "text-black"
                    )}>
                      {account.businessName || "N/A"}
                    </TH>
                    <TH className={twMerge(
                      "font-normal",
                      targets.includes(account?._id) ? "text-white" : "text-black"
                    )}>
                      {account.phone}
                    </TH>
                    <TH className={twMerge(
                      "font-normal",
                      targets.includes(account?._id) ? "text-white" : "text-black"
                    )}>
                      {account.state || "N/A"}
                    </TH>
                  </TR>
                ))}
              </TBody>
            </DataTable>
          </div>
        </div>
      )}
      <div className="mt-4">
        <Pagination
          page={totalPages}
          handlePageClick={(e: { selected: number }) =>
            setCurrentPage(e.selected + 1)
          }
        />
      </div>
    </div>
  );
};
